import EventDispatcher from '@/core/event/eventDispatcher';
import ViewEvent from '@/core/event/viewEvent';
import LogUtil from '@/core/utils/logUtil';
import type AbstractView from "@/core/view/abstractView";
import type ViewFactory from "@/app/views/viewFactory";

let singleton = Symbol();
let singletonEnforcer = Symbol();

class ViewController {

    private _targetNode?:HTMLElement;
    private _currentView?:AbstractView|null;
    private _isTransitionRunning:boolean = false;
    private _viewFactory?:ViewFactory;


    constructor(enforcer) {
        if (enforcer !== singletonEnforcer) {
            throw new Error('Cannot construct ViewController singleton');
        }

        EventDispatcher.addEventListener(ViewEvent.LOAD, event => this.loadView(event));
        EventDispatcher.addEventListener(ViewEvent.UNLOAD, () => this.hideCurrentView(() => this._removeCurrentView()));
    }

    static get instance() {
        if (!this[singleton]) {
            this[singleton] = new ViewController(singletonEnforcer);
        }

        return this[singleton];
    }

    initialize(targetNode, viewFactory) {
        this._targetNode = targetNode;
        this._viewFactory = viewFactory;
    }

    loadView(event:ViewEvent) {

        if(this._isTransitionRunning) {
            return;
        }

        this._isTransitionRunning = true;

        // create new view
        let id = event.data.id;
        let params = event.data.params;
        let view = this._viewFactory?.getViewInstanceById(id, params);

        if (!view) {
            LogUtil.error(this.constructor.name, 'loadview - view does not exists');
            return;
        }

        LogUtil.logClass(this,'Load new view:', view.constructor.name);

        EventDispatcher.dispatchEvent(new ViewEvent(ViewEvent.START_LOADING));

        // render the template before preloading
        view.render();
        this._targetNode?.appendChild(view.element);

        view.preload(() => this._onViewPreloadComplete(view));
    }

    _onViewPreloadComplete(viewInstance) {
        LogUtil.logClass(this, 'New view preload complete:', viewInstance.constructor.name);

        EventDispatcher.dispatchEvent(new ViewEvent(ViewEvent.LOADING_COMPLETE));
        this.hideCurrentView(() => this._showNewView(viewInstance));
    }

    hideCurrentView(callback) {

        if(this._currentView) {
            LogUtil.logClass(this, 'Hide current view:', this._currentView.constructor.name);
            this._currentView.hide(() => callback());
        } else {
            callback();
        }
    }

    _showNewView(view) {
        if(this._currentView) {
            this._removeCurrentView();
        }

        LogUtil.logClass(this, 'Show new view:', view.constructor.name);

        this._currentView = view;
        this._currentView?.show(() => this._showViewCallback(view.id));
        this._currentView?.init();
    }

    _showViewCallback(viewId) {
        this._isTransitionRunning = false;
        EventDispatcher.dispatchEvent(new ViewEvent(ViewEvent.CHANGED, viewId));
    }

    _removeCurrentView() {
        LogUtil.logClass(this, 'Remove current view:', this._currentView?.constructor.name);

        this._currentView?.destroy();
        if (this._currentView?.element){
            this._targetNode?.removeChild(this._currentView?.element);
        }

        this._currentView = null;

    }
}

export default ViewController;
